'use client'
import React, { ReactNode, useEffect } from 'react'
import { useHCRouter } from '@/components/HCNextLink'
import { AuthProvider, TAuthOptions } from 'core/components/Authenticate'
// import { usePathname, useSearchParams } from 'next/navigation';
import { getSearchParams } from 'core/utils'
import { getLoginPath, getRegisterPath } from 'core/controller/auth/login'

import TelegramLoginPC from '@/components/TelegramLoginPC'
import notification from '@/components/notification'
import { useLocale } from 'core/components/Locale'
import GoogleLoginPC from './GoogleLoginPC'
import AppleLoginPC from './AppleLoginPC'
import { syncLoginTokenToMSite } from 'core/controller/auth/tokenBridge'
import { useResponsiveDevice } from 'core/utils/useResponsiveDevice'
import { isInApp } from 'core/utils/appBridge'
import { usePostUserLogoutMutation } from 'core/query/fixLegacyAPI/hk-web/Hotcoin/query'

export default function LoginProvider({
  children,
}: Readonly<{
  children: ReactNode
}>) {
  const router = useHCRouter()
  const responsiveDevice = useResponsiveDevice()
  const { mutateAsync: postUserLogout } = usePostUserLogoutMutation()
  // const searchParams = useSearchParams();
  // 当登录成功时，负责处理登录逻辑的组件通过调用鉴权组件，
  // 接口数据回调给此处onLogin，由onLogin统一处理登录后的数据逻辑
  // 由于各端的onLogin处理逻辑不同，可以通过此处解耦，如native没有localStorage
  const onLogin = async (data: any) => {
    localStorage.setItem('token', data.data.token)
    localStorage.setItem('userInfo', JSON.stringify(data.data.userinfo))
    const redirect = getSearchParams('redirect')
    if (responsiveDevice !== 'phone' || isInApp()) {
      if (data?.disableRedirect) {
        return
      }
      if (redirect) {
        router.replace(redirect, { target: '_self' })
      } else {
        router.replace('/', { target: '_self' })
      }
    } else {
      // 同步 token 到移动端
      syncLoginTokenToMSite(data.data.token, JSON.stringify(data.data.userinfo), redirect)
        .then((result) => {
          // onLoginSuccess?.({ ...data, disableRedirect: !!result?.disableRedirect })
          const redirect = getSearchParams('redirect')

          if (data?.disableRedirect && !!result?.disableRedirect) {
            return
          }
          if (redirect) {
            router.replace(redirect, { target: '_self' })
          } else {
            router.replace('/', { target: '_self' })
          }
        })
        .catch((error) => {
          // todo: 处理错误接入sentry
          console.error('Failed to sync token:', error)
        })
    }
  }

  // 当登出成功时，负责处理登出逻辑的组件通过调用鉴权组件，由onLogout统一处理登出后的数据逻辑
  // 由于各端的onLogin处理逻辑不同，可以通过尺寸解耦，如native没有localStorage
  const onLogout = async () => {
    await postUserLogout({})
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
  }

  const handleRegister = (options?: TAuthOptions) => {
    const registerPath = getRegisterPath(options)
    if (registerPath) {
      router.push(registerPath)
    }
  }

  const handleLogin = (loginOptions?: TAuthOptions) => {
    const loginPath = getLoginPath(loginOptions)
    if (loginPath) {
      router.push(loginPath)
    }
  }

  const updateLoginData = () => {
    try {
      if (!localStorage.getItem('userInfo') || !localStorage.getItem('token')) return

      return JSON.parse(localStorage.getItem('userInfo') as string)
    } catch (e) {
      return
    }
  }

  const getThirdPartyLoginComponents = (updateLoginData: () => void) => {
    const onSuccess = (res, thirdLoginResponse) => {
      console.log('getThirdPartyLoginComponents login res', res)
      const data = res.data.data

      if (data.bindStatus) {
        onLogin({
          data: {
            token: data.loginResponse.token,
            userinfo: data.loginResponse.userinfo,
          },
        })
        updateLoginData()
      } else {
        //没有绑定账号
        // todo: 目测以前方案有问题
        // sessionStorage.setItem('thirdLoginParseAuthInfo', JSON.stringify(data))
        router.push(
          `/bindAccount?parseAuthInfo=${JSON.stringify(data)}&originalAuthInfo=${JSON.stringify(thirdLoginResponse)}`
        )
      }
    }
    const isMainSite =
      typeof window !== 'undefined' && ['www.hotcoin.com', 'www.htcoinlab.top'].includes(window.location.host)

    return {
      telegram: (props: { className: string }) => {
        if (isMainSite) {
          return (
            <TelegramLoginPC onSuccess={onSuccess} className={props.className}>
              {props.children}
            </TelegramLoginPC>
          )
        }
        return null
      },
      google: (props) => {
        return (
          <GoogleLoginPC onSuccess={onSuccess} className={props.className}>
            {props.children}
          </GoogleLoginPC>
        )
      },
      apple: (props) => {
        return (
          <AppleLoginPC onSuccess={onSuccess} className={props.className}>
            {props.children}
          </AppleLoginPC>
        )
      },
    }
  }
  // const pathname = usePathname();

  // // 登录失效时，清除token和userInfo，跳转到登录页
  // useEventListener('loginInvalid', () => {
  //   localStorage.removeItem('token');
  //   localStorage.removeItem('userInfo');
  //   if (pathname.includes('/login')) {
  //     return;
  //   }
  //   router.push('/login?redirect=' + pathname);
  // }, { target: window });

  return (
    <AuthProvider
      onLogin={onLogin}
      onLogout={onLogout}
      handleLogin={handleLogin}
      handleRegister={handleRegister}
      updateLoginData={updateLoginData}
      getThirdPartyLoginComponents={getThirdPartyLoginComponents}
    >
      {children}
    </AuthProvider>
  )
}
